'use client';

import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Search_RequestBody } from '@opensearch-project/opensearch/api';
import { QueryContainer } from '@opensearch-project/opensearch/api/_types/_common.query_dsl';
import { TotalHits } from '@opensearch-project/opensearch/api/_types/_core.search';
import { Sort } from '@opensearch-project/opensearch/api/_types/_common';

import { InitialProductQueryFilters } from '@/constants';
import { EnumCustomerType, TreezStore } from '@/types';

import { OpenSearchContext } from './OpenSearchContext';
import { useSearch } from '../hooks';
import {
  buildFacetQueries,
  buildPriceFilterFromQueryRecord,
  buildQueryFilterFromSearchParams,
  buildSearchQueryFilter,
  buildSmartFacetAggregations,
} from '../utils';
import { DefaultSortingFilter } from '../constants';

const DEFAULT_PAGE_SIZE = 20;

interface ProviderProps extends PropsWithChildren {
  initialQueryFiltersByFacets?: Record<string, QueryContainer>;
  initialFixedQueryFiltersByFacets?: Record<string, QueryContainer>;
  initialSortFilter?: Sort;
  initialSearchFilter?: string;
  id?: string;
  selectedStore?: TreezStore;
  selectedCustomerType?: EnumCustomerType;
  includePromoItems?: boolean;
  initialPage?: number;
}

const OpenSearchProvider: FC<ProviderProps> = ({
  children,
  initialQueryFiltersByFacets = {},
  initialFixedQueryFiltersByFacets = {},
  initialSortFilter,
  initialSearchFilter = '',
  id,
  selectedCustomerType,
  selectedStore,
  includePromoItems = false,
  initialPage = 0,
}) => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [size, setSize] = useState(pageSize);
  const [from, setFrom] = useState<number>(initialPage * pageSize);
  const [sortFilter, setSortFilter] = useState<Sort | undefined>(
    initialSortFilter,
  );
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [searchableAtributes, setSearchableAttributes] = useState<
    string[] | undefined
  >(undefined);
  const [queryFiltersByFacets, setQueryFiltersByFacets] = useState(
    initialQueryFiltersByFacets,
  );
  const [fixedQueryFiltersByFacets, setFixedQueryFiltersByFacets] = useState(
    initialFixedQueryFiltersByFacets,
  );

  useEffect(() => {
    setQueryFiltersByFacets(initialQueryFiltersByFacets);
  }, [initialQueryFiltersByFacets]);

  useEffect(() => {
    setFixedQueryFiltersByFacets(initialFixedQueryFiltersByFacets);
  }, [initialFixedQueryFiltersByFacets]);

  useEffect(() => {
    setSortFilter(initialSortFilter);
  }, [initialSortFilter]);

  useEffect(() => {
    setSearchFilter(initialSearchFilter);
  }, [initialSearchFilter]);

  useEffect(() => {
    setFrom(initialPage * pageSize);
  }, [initialPage, pageSize]);

  const priceFilter = buildPriceFilterFromQueryRecord(
    queryFiltersByFacets,
    includePromoItems,
  );

  const queryFiltersRecord = useMemo(
    () => ({
      ...buildSearchQueryFilter(searchFilter),
      ...buildQueryFilterFromSearchParams(InitialProductQueryFilters),
      ...queryFiltersByFacets,
      ...fixedQueryFiltersByFacets,
      ...priceFilter,
    }),
    [queryFiltersByFacets, fixedQueryFiltersByFacets, searchFilter],
  );

  const searchBody: Search_RequestBody = {
    from,
    size,
    query: buildFacetQueries(queryFiltersRecord),
    aggs: buildSmartFacetAggregations(queryFiltersRecord),
    ...(searchFilter
      ? {}
      : sortFilter
      ? { sort: sortFilter }
      : { sort: DefaultSortingFilter }),
  };

  const { data, isLoading, status, refetch, isFetching } = useSearch({
    searchBody,
    id,
    selectedCustomerType,
    selectedStore,
  });

  const hits = data?.hits?.hits ?? [];
  const totalHits = (data?.hits?.total as TotalHits)?.value ?? 0;
  const aggregations = data?.aggregations ?? {};

  useEffect(() => {
    if (isLoading || isFetching) return;
    if (totalHits < from) setFrom(0);
  }, [totalHits, from]);

  return (
    <OpenSearchContext
      value={{
        searchBody,
        hits,
        totalHits,
        aggregations,
        queryFiltersByFacets,
        updateQueryFiltersByFacets: setQueryFiltersByFacets,
        fixedQueryFiltersByFacets,
        updateFixedQueryFiltersByFacets: setFixedQueryFiltersByFacets,
        isLoading,
        isFetching,
        pageSize,
        updatePageSize: setPageSize,
        updateSize: setSize,
        from,
        updateFrom: setFrom,
        searchableAtributes,
        updateSearchableAttributes: setSearchableAttributes,
        status,
        refetch,
        sortFilter,
        updateSortFilter: setSortFilter,
        searchFilter,
        updateSearchFilter: setSearchFilter,
      }}
    >
      {children}
    </OpenSearchContext>
  );
};

export { OpenSearchProvider };
